import { createFileRoute, Link, notFound, type ErrorComponentProps } from "@tanstack/react-router";
import { VENUES, type Venue } from "@/data/venues";
import { useReveal } from "@/hooks/use-reveal";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA, absoluteImageUrl } from "@/lib/schema-org";
const BASE = "https://dj-val.fr";

export const Route = createFileRoute("/lieux/$slug")({
  loader: ({ params }) => {
    const venue = VENUES[params.slug];
    if (!venue) throw notFound();
    return { venue };
  },
  head: ({ loaderData }) => {
    const v = loaderData?.venue as Venue | undefined;
    if (!v) return { meta: [{ title: "Lieu de réception" }] };
    const url = `${BASE}/lieux/${v.slug}`;
    return {
      meta: [
        { title: v.metaTitle },
        { name: "description", content: metaDesc(v.metaDescription) },
        { property: "og:title", content: v.metaTitle },
        { property: "og:description", content: metaDesc(v.metaDescription) },
        { property: "og:image", content: absoluteImageUrl(v.hero) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dj-val.fr/lieux/${v.slug}` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: absoluteImageUrl(v.hero) },
        { name: "author", content: "DJ Val" },
      ],
      links: [
        { rel: "canonical", href: `https://dj-val.fr/lieux/${v.slug}` },
        { rel: "preload", as: "image", href: v.hero, fetchpriority: "high" },
      ],
      scripts: [
        // Schema Place (le lieu lui-même)
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            name: v.name,
            description: v.intro,
            address: {
              "@type": "PostalAddress",
              addressLocality: v.city,
              addressRegion: v.region,
              postalCode: v.postalCode,
              addressCountry: "FR",
            },
            image: v.hero,
            ...(v.geo && {
              geo: { "@type": "GeoCoordinates", latitude: v.geo.lat, longitude: v.geo.lng },
            }),
            ...(v.site && { url: v.site }),
          }),
        },
        // Schema LocalBusiness (DJ Val pour ce lieu)
        {
          type: "application/ld+json",
          children: JSON.stringify({ ...LOCAL_BUSINESS_SCHEMA, "@context": "https://schema.org", "@id": `${url}#djval`, name: `DJ Val — Prestation au ${v.name}`, url, image: v.hero, areaServed: { "@type": "Place", name: `${v.name}, ${v.city}` }, knowsAbout: [v.name, `Mariage ${v.city}`, "Sonorisation", "Éclairage"] }),
        },
        // FAQ
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: v.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        // Article (pour le contenu rédigé — EEAT)
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: v.metaTitle,
            description: v.metaDescription,
            image: v.hero,
            author: {
              "@type": "Person",
              name: "DJ Val",
              url: `${BASE}/a-propos`,
              jobTitle: "DJ professionnel — 20 ans d'expérience",
            },
            publisher: ORGANIZATION_SCHEMA,
            mainEntityOfPage: url,
          }),
        },
        // Breadcrumb
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: BASE },
              { "@type": "ListItem", position: 2, name: "Lieux de réception", item: `${BASE}/lieux` },
              { "@type": "ListItem", position: 3, name: v.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground">
      <div className="text-center">
        <h1 className="font-serif-display text-4xl">Lieu non référencé</h1>
        <Link to="/lieux" className="mt-4 inline-block text-[var(--gold)] underline">
          Voir tous les lieux
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }: ErrorComponentProps) => (
    <div className="min-h-dvh flex items-center justify-center bg-background text-foreground p-6">
      <div className="text-center max-w-md">
        <h1 className="font-serif-display text-3xl">Une erreur est survenue</h1>
        <p className="mt-4 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        <Link to="/lieux" className="mt-6 inline-block text-[var(--gold)] underline">
          Retour aux lieux
        </Link>
      </div>
    </div>
  ),
  component: VenuePage,
});

function VenuePage() {
  useReveal();
  const { venue } = Route.useLoaderData();
  const v: Venue = venue;

  return (
    <div className="bg-background text-muted-foreground">
      {/* HERO */}
      <section className="on-dark relative min-h-[80vh] flex items-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img
            src={v.hero}
            alt={v.heroAlt}
            className="h-full w-full object-cover opacity-40 kenburns-img"
            fetchPriority="high"
            decoding="async"
            loading="eager"
            width={1920}
            height={1080}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, var(--ink) 0%, color-mix(in oklab, var(--ink) 70%, transparent) 55%, color-mix(in oklab, var(--ink) 30%, transparent) 100%), linear-gradient(to bottom, var(--ink) 0%, transparent 40%, var(--ink) 100%)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-3xl reveal space-y-6">
          <p className="eyebrow">
            Lieu de réception · {v.city} — {v.region}
          </p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            <span className="italic font-light">DJ au</span>
            <span className="block font-light gold-text-shine mt-2">{v.name}</span>
          </h1>
          <span className="gold-rule !w-12" />
          <p className="text-base text-foreground/85 font-light leading-relaxed max-w-xl">
            {v.intro}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contactez-nous"
              className="bg-[var(--gold)] text-[var(--ink)] px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Devis pour ce lieu
            </Link>
            <a
              href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "lieux_detail" })}
              className="border border-[var(--gold-line)] text-foreground px-8 py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
            >
              07 67 90 86 67
            </a>
          </div>
          {v.prestationsRealisees && (
            <p className="text-xs text-[var(--gold)] uppercase tracking-[0.2em] pt-4">
              ✦ {v.prestationsRealisees} prestations réalisées sur ce lieu
            </p>
          )}
        </div>
      </section>
      <ReviewsProof />

      {/* ANECDOTE / EXPERIENCE (signal EEAT fort) */}
      <section className="py-24 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-3xl mx-auto sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Mon expérience sur place</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
            {v.anecdote.title}
          </h2>
          <span className="gold-rule !w-12 my-6" />
          <p className="font-serif-display italic text-lg text-foreground/90 leading-relaxed">
            {v.anecdote.body}
          </p>
          <p className="mt-6 text-xs text-muted-foreground uppercase tracking-[0.2em]">
            — DJ Val, 20 ans d'expérience en Provence
          </p>
        </div>
      </section>

      {/* EXPERTISE TERRAIN */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Expertise du lieu</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Pourquoi je <span className="italic">connais</span> {v.shortName} par cœur
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-[var(--gold-line)] md:grid-cols-3 border border-[var(--gold-line)]">
          {[
            { title: "Acoustique", text: v.expertise.acoustique, num: "01" },
            { title: "Logistique", text: v.expertise.logistique, num: "02" },
            { title: "Timing soirée", text: v.expertise.moments_cles, num: "03" },
          ].map((b, i) => (
            <div
              key={b.title}
              className="bg-background p-10 sr"
              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">{b.num}</p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{b.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CARACTÉRISTIQUES DU LIEU */}
      <section className="py-20 px-6 border-y border-[var(--gold-line)] bg-card">
        <div className="max-w-5xl mx-auto sr" data-reveal>
          <div className="text-center mb-12">
            <p className="eyebrow text-[var(--gold)]">Le lieu en bref</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">
              {v.name} <span className="italic">en chiffres</span>
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="border border-[var(--gold-line)] bg-background p-6 space-y-4">
              {v.caracteristiques.capaciteAssis && (
                <div className="flex justify-between gap-4 pb-3 border-b border-[var(--gold-line)]">
                  <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    Capacité assise
                  </span>
                  <span className="font-serif-display text-foreground text-sm">
                    {v.caracteristiques.capaciteAssis}
                  </span>
                </div>
              )}
              {v.caracteristiques.capaciteCocktail && (
                <div className="flex justify-between gap-4 pb-3 border-b border-[var(--gold-line)]">
                  <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    Capacité cocktail
                  </span>
                  <span className="font-serif-display text-foreground text-sm">
                    {v.caracteristiques.capaciteCocktail}
                  </span>
                </div>
              )}
              <div className="flex justify-between gap-4 pb-3 border-b border-[var(--gold-line)]">
                <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Style
                </span>
                <span className="font-serif-display text-foreground text-sm text-right">
                  {v.caracteristiques.style}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Saison
                </span>
                <span className="font-serif-display text-foreground text-sm text-right">
                  {v.caracteristiques.saison}
                </span>
              </div>
            </div>
            <div className="border border-[var(--gold-line)] bg-background p-6">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-4">
                Espaces disponibles
              </p>
              <ul className="space-y-2">
                {v.caracteristiques.espaces.map((esp) => (
                  <li
                    key={esp}
                    className="flex items-center gap-3 text-sm text-foreground/85 font-light"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />
                    {esp}
                  </li>
                ))}
              </ul>
              {v.site && (
                <a
                  href={v.site}
                  target="_blank"
                  rel="noopener"
                  className="mt-6 inline-flex items-center text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-foreground transition-colors"
                >
                  Site officiel du domaine →
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CONSEILS PRO */}
      <section className="py-24 px-6 max-w-5xl mx-auto">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Mes conseils pour ce lieu</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            Ce que je <span className="italic">recommande</span> aux couples
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {v.conseils.map((c, i) => (
            <div
              key={c.titre}
              className="border border-[var(--gold-line)] bg-card p-8 sr"
              data-reveal
              style={{ transitionDelay: `${(i % 2) * 80}ms` }}
            >
              <p className="font-serif-display italic text-[var(--gold)] text-2xl">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif-display text-xl text-foreground mt-3">{c.titre}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{c.texte}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 max-w-4xl mx-auto border-t border-[var(--gold-line)]">
        <div className="text-center mb-12 sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">Questions fréquentes</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            DJ au {v.shortName}
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="space-y-4">
          {v.faq.map((f) => (
            <details key={f.q} className="group border border-[var(--gold-line)] bg-card p-6 sr" data-reveal>
              <summary className="cursor-pointer font-serif-display text-xl text-foreground flex justify-between items-center gap-4">
                {f.q}
                <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center border-t border-[var(--gold-line)]">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            Votre mariage au <span className="italic text-[var(--gold)]">{v.name}</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm text-muted-foreground font-light mb-8">
            Je connais ce lieu sur le bout des doigts. Parlons de votre projet — devis personnalisé
            sous 24 h, sans engagement.
          </p>
          <Link
            to="/contactez-nous"
            className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Demander un devis
          </Link>
        </div>
      </section>

      {/* Autres lieux */}
      <section className="py-20 px-6 border-t border-[var(--gold-line)] bg-card">
        <div className="max-w-6xl mx-auto sr" data-reveal>
          <p className="eyebrow text-[var(--gold)] text-center">Autres lieux où j'interviens</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {Object.values(VENUES)
              .filter((other) => other.slug !== v.slug)
              .map((other) => (
                <Link
                  key={other.slug}
                  to="/lieux/$slug"
                  params={{ slug: other.slug }}
                  className="px-5 py-2.5 border border-[var(--gold-line)] text-foreground/90 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
                >
                  {other.name}
                </Link>
              ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/lieux"
              className="inline-block text-[var(--gold)] text-sm uppercase tracking-[0.2em] hover:text-foreground transition-colors"
            >
              Voir tous les lieux →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
